/**
 * The owner's team overview (BUILD_PLAN.md section 11.2): everyone by their
 * last 14 days. Each cell is that person's own day, with the day view's
 * numbers: time captured, time logged, and blocks still to review. A cell
 * opens that day.
 *
 * Closed is the goal, not logged (ADR 0185): each person shows their open
 * tickets now and the oldest's age, and each day's bar splits its logged
 * time on tickets still open from the rest.
 *
 * It refreshes as the day view does, every 30 seconds while the tab is visible.
 */
import { useQuery } from "@tanstack/react-query";
import { useId, useLayoutEffect, useRef } from "react";
import { Link } from "react-router";

import { ApiError } from "../../api/client";
import type { DayStats, Team, TeamDay, TeamMember } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Badge } from "../../components/ui/Badge";
import { Panel } from "../../components/ui/Panel";
import { formatDayParts, formatLongDay } from "../../lib/dates";
import { formatDuration } from "../../lib/durations";
import { POLL_MS } from "../DayPage";

function personName(member: TeamMember): string {
  return member.display_name ?? member.email ?? "Unnamed user";
}

function isEmpty(stats: DayStats): boolean {
  return stats.captured_seconds === 0 && stats.logged_seconds === 0 && stats.to_review === 0;
}

/** The logged time's split by its tickets' state (ADR 0185), in words; none when nothing is on a ticket. */
function ticketSplit(day: TeamDay): string {
  const parts = [
    day.closed_ticket_seconds > 0 ? `${formatDuration(day.closed_ticket_seconds)} on closed tickets` : null,
    day.open_ticket_seconds > 0 ? `${formatDuration(day.open_ticket_seconds)} on open tickets` : null,
  ].filter((part) => part !== null);
  return parts.length > 0 ? ` (${parts.join(", ")})` : "";
}

/** Whose day a cell is: "Jordan Example, Thursday, October 1, 2026". */
function whoseDay(member: TeamMember, day: TeamDay): string {
  return `${personName(member)}, ${formatLongDay(day.day)}`;
}

/** The words after a cell's count of blocks to review, and whose day it is. */
function toReviewAndWhose(member: TeamMember, day: TeamDay): string {
  return `${day.stats.to_review === 1 ? "block" : "blocks"} to review. ${whoseDay(member, day)}`;
}

/**
 * What a cell says, in words: its link's name, and its hover text. Its
 * numbers come first, as it shows them, so the name holds what's on it
 * (WCAG 2.5.3): "2h 05m captured, 1h 40m logged, 4 blocks to review. Jordan
 * Example, Thursday, October 1, 2026". The link takes it from its content,
 * the words around the numbers there for a screen reader only.
 */
function cellLabel(member: TeamMember, day: TeamDay): string {
  const { stats } = day;
  return isEmpty(stats)
    ? `Nothing captured or logged. ${whoseDay(member, day)}`
    : `${formatDuration(stats.captured_seconds)} captured, ${formatDuration(stats.logged_seconds)} logged` +
        `${ticketSplit(day)}, ${String(stats.to_review)} ${toReviewAndWhose(member, day)}`;
}

/** "Open tickets 3 · oldest 6 d": a person's open tickets now (ADR 0185). */
/**
 * "Open tickets 3 · oldest 6 d": it wraps at the dot in a narrow column,
 * where it was cut to "oldest …", the age it's there for (#866).
 */
function OpenTickets({ member }: { member: TeamMember }) {
  const oldest = member.oldest_open_ticket_days;
  return (
    <span className="block text-[12.5px] text-muted">
      <span className="whitespace-nowrap">{`Open tickets ${String(member.open_tickets)}`}</span>
      {member.open_tickets > 0 && oldest !== null ? (
        <>
          {" · "}
          <span className="whitespace-nowrap">{`oldest ${String(oldest)} d`}</span>
        </>
      ) : null}
    </span>
  );
}

/** A share of the captured time, as a bar's width in percent. */
function shareOf(seconds: number, captured: number): number {
  return captured > 0 ? Math.min(100, (seconds / captured) * 100) : 0;
}

/**
 * One person's day: captured, logged and a bar of the share logged, and
 * what's to review. The bar splits the logged time on tickets still open
 * (amber) from the rest: on closed tickets, or on none.
 */
function DayCell({ member, day }: { member: TeamMember; day: TeamDay }) {
  const { stats } = day;
  const label = cellLabel(member, day);
  const share = shareOf(stats.logged_seconds, stats.captured_seconds);
  const open = Math.min(share, shareOf(day.open_ticket_seconds, stats.captured_seconds));
  const search = new URLSearchParams({ user: member.id, date: day.day });
  return (
    <Link
      to={{ pathname: "/", search: `?${search.toString()}` }}
      // No aria-label: its name is what it shows, said in words (cellLabel).
      title={label}
      className={
        "flex h-full min-h-[84px] flex-col gap-1 rounded-row border border-line bg-panel-2 px-1.5 py-2 " +
        "whitespace-nowrap hover:border-brand"
      }
    >
      {isEmpty(stats) ? (
        <>
          <span aria-hidden="true" className="text-muted">
            —
          </span>
          <span className="sr-only">{label}</span>
        </>
      ) : (
        <>
          {/* The spaces sit between the elements, where every name's reckoning keeps them. */}
          <span className="text-[15px] leading-none font-semibold tracking-[-0.04em]">
            {formatDuration(stats.captured_seconds)} <span className="sr-only">captured,</span>
          </span>{" "}
          <span className="text-[12.5px] leading-none text-muted">
            {formatDuration(stats.logged_seconds)} <span className="sr-only">{`logged${ticketSplit(day)},`}</span>
          </span>{" "}
          {/* The Capture gap tile's bar, small, its open tickets' share amber: said in words in the link's name. */}
          <span aria-hidden="true" className="flex h-1.5 overflow-hidden rounded-pill bg-track">
            <span
              data-share="closed"
              className="block h-full bg-brand"
              style={{ width: `${String(Math.round(share - open))}%` }}
            />
            <span
              data-share="open"
              className="block h-full bg-status-needs-client-fg"
              style={{ width: `${String(Math.round(open))}%` }}
            />
          </span>
          {stats.to_review > 0 ? (
            <Badge tone="needs-client">{stats.to_review}</Badge>
          ) : (
            <span className="sr-only">0</span>
          )}{" "}
          <span className="sr-only">{toReviewAndWhose(member, day)}</span>
        </>
      )}
    </Link>
  );
}

function TeamGrid({ team }: { team: Team }) {
  const { me } = useSession();
  const legendId = useId();
  const today = team.days.at(-1);
  // Narrower than the grid, it opens on the latest days, names kept in view.
  const scroller = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (scroller.current) {
      scroller.current.scrollLeft = scroller.current.scrollWidth;
    }
  }, []);
  return (
    <>
      <p id={legendId} className="text-[13px] text-muted">
        Each day shows the time captured, then the time logged, with a bar of the share logged (its time on
        tickets still open in amber; closed is the goal), and <Badge tone="needs-client">n</Badge> blocks still to
        review. Each is that person&apos;s own day, in their time zone. Open one to see the day. Under each name,
        their open tickets now and the oldest&apos;s age.
      </p>
      {/* At 1280 px the grid fits the page; narrower, it scrolls inside its panel (section 11.3). */}
      <div ref={scroller} className="mt-3 overflow-x-auto">
        <table
          aria-describedby={legendId}
          className="w-full min-w-[1040px] table-fixed border-separate border-spacing-1 text-left"
        >
          <caption className="sr-only">Each person by their last 14 days</caption>
          <colgroup>
            <col className="w-[148px]" />
            {team.days.map((day) => (
              <col key={day} />
            ))}
          </colgroup>
          <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-panel pb-1 align-bottom">
                Person
              </th>
              {team.days.map((day) => {
                const { weekday, date } = formatDayParts(day);
                return (
                  <th key={day} scope="col" className="px-1.5 pb-1 align-bottom font-medium">
                    <span className="block">{day === today ? "Today" : weekday}</span>
                    <span className="block whitespace-nowrap normal-case tracking-normal">{date}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {team.members.map((member) => (
              <tr key={member.id}>
                <th scope="row" className="sticky left-0 z-10 bg-panel pr-2 align-middle font-normal">
                  <span className="block truncate font-medium" title={personName(member)}>
                    {personName(member)}
                    {member.id === me.id ? <span className="font-normal text-muted"> (you)</span> : null}
                  </span>
                  {member.timezone !== me.timezone ? (
                    <span className="block truncate text-[12.5px] text-muted">{member.timezone}</span>
                  ) : null}
                  <OpenTickets member={member} />
                  {!member.is_active ? <Badge tone="neutral">Inactive</Badge> : null}
                </th>
                {member.days.map((day) => (
                  <td key={day.day} className="h-full p-0 align-top">
                    <DayCell member={member} day={day} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function TeamPage() {
  const { api } = useSession();
  const team = useQuery({
    queryKey: ["team"],
    queryFn: () => api.getTeam(),
    refetchInterval: POLL_MS,
    // A refusal (4xx) won't pass on a second try.
    retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
  });

  let body;
  if (team.data) {
    body = <TeamGrid team={team.data} />;
  } else if (team.error) {
    body = (
      <p role="alert" className="text-muted">
        The team overview could not be loaded. Reload the page to try again.
      </p>
    );
  } else {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  }

  return (
    <>
      <Panel>
        <h1 className="text-xl font-semibold tracking-tight">Team</h1>
        <p className="mt-1 text-muted">
          Everyone&apos;s last 14 days: what was captured, what was logged, and what&apos;s left to review.
        </p>
      </Panel>
      {team.data && team.isError ? (
        <p role="status" className="rounded-row border border-line bg-panel px-4 py-2.5 text-[13px] text-muted">
          Couldn&apos;t refresh the team. Trying again every 30 seconds.
        </p>
      ) : null}
      <Panel aria-label="Each person's last 14 days">{body}</Panel>
    </>
  );
}
